import React, { useCallback, useEffect, useState } from "react";
import {
  Container,
  Fab,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  Divider,
  Box,
  TextField,
  FormControl,
  Button,
  CardContent,
  Card,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Add, MoreVert } from "@material-ui/icons";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import Title from "../../ui/Title";
import FloatingActions from "../../ui/FloatingActions";
import Confirm from "../../ui/Confirm";

import { posts } from "../../messages/posts";
import { confirm as confirmMsgs, common } from "../../messages";
import { MANAGER_ROUTES } from "../../utils/constants";
import { useBmapi } from "../../utils/bmapi-context";
import { getErrorMessageString } from "../../utils/errors";
import { extractFirstText } from "../../utils/postUtils";
import ImageManagerDialog from "../../components/ImageManagerDialog";

const useStyles = makeStyles(() => ({
  primaryClamp2: {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
}));

const INIT_QUERY = {
  title: "",
};

export default function ManagePostList() {
  const classes = useStyles();
  const intl = useIntl();
  const history = useHistory();
  const { bmapi, notifyError, notifySuccess, businessId } = useBmapi();

  const [query, setQuery] = useState(INIT_QUERY);
  const [postList, setPostList] = useState([]);
  const [filteredPostList, setFilteredPostList] = useState([]);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  // conferma eliminazione
  const [deleteRequest, setDeleteRequest] = useState(false);

  const loadPostsList = useCallback(() => {
    if (!bmapi) return;
    bmapi
      .getPosts({
        business: businessId,
        lang: bmapi.getUserInfo().business.default_language,
      })
      .then((resp) => {
        const list = resp || [];
        const sorted = [...list].sort((a, b) =>
          (extractFirstText(a) || "").localeCompare(
            extractFirstText(b) || "",
            intl.locale || undefined,
            { sensitivity: "base" }
          )
        );
        setPostList(sorted || []);
        setFilteredPostList(sorted || []);
      })
      .catch((e) => notifyError(getErrorMessageString(e, intl)));
  }, [bmapi, intl, notifyError, businessId]);

  useEffect(() => {
    loadPostsList();
  }, [loadPostsList]);

  const handleAddNewPost = () => {
    history.push(MANAGER_ROUTES.POST);
  };

  const handleMenuOpen = (event, postId) => {
    setAnchorEl(event.currentTarget);
    setSelectedPostId(postId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    history.push(`${MANAGER_ROUTES.POST}?id=${selectedPostId}`);
    handleMenuClose();
  };

  const handleDuplicate = () => {
    history.push(`${MANAGER_ROUTES.POST}?cloneId=${selectedPostId}`);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteRequest(true); // apri conferma
    handleMenuClose();
  };

  const handleQuery = useCallback((label) => {
    const updateValue = (val) => {
      setQuery((v) => ({ ...v, [label]: val }));
    };
    return (i) => {
      if (i?.target) updateValue(i.target.value);
      else updateValue(i);
    };
  }, []);

  const onDeletePost = async () => {
    if (!selectedPostId) return;
    try {
      await bmapi.deletePost(selectedPostId);
      notifySuccess(intl.formatMessage(common.deleteElement));
      setDeleteRequest(false);
      setSelectedPostId(null);
      loadPostsList();
    } catch (e) {
      notifyError(getErrorMessageString(e, intl));
    }
  };

  const resetFilters = () => {
    setQuery(INIT_QUERY);
    loadPostsList();
  };

  const submitQuery = (e) => {
    e.preventDefault();

    const newPostList = postList.filter((post) =>
      post.groups[0].blocks[0].content.content
        .toLowerCase()
        .includes(query.title.toLowerCase())
    );
    setFilteredPostList(newPostList);
    setQuery((v) => ({ ...v }));
  };

  // TODO: non modificare la lista originale col filtro

  return (
    <Container maxWidth="md">
      <Title>
        <FormattedMessage id="navigation.manager.posts" defaultMessage="Post" />
      </Title>

      <ImageManagerDialog bmapi={bmapi} folder="posts" />

      <Box my={2}>
        <Card>
          <CardContent>
            <form onSubmit={submitQuery}>
              <TextField
                margin="dense"
                label="Filtra per titolo"
                value={query.title}
                onChange={handleQuery("title")}
                fullWidth
                helperText={
                  query.title &&
                  query.title.length > 0 &&
                  query.title.length < 3
                    ? "Inserisci almeno 3 caratteri per una ricerca più precisa."
                    : ""
                }
              />
              <Box mt={1}>
                <FormControl
                  margin="normal"
                  style={{
                    float: "right",
                    display: "flex",
                    flexDirection: "row",
                    gap: 8,
                  }}
                >
                  <Button variant="contained" onClick={resetFilters}>
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={query.title.length < 3}
                  >
                    {intl.formatMessage(common.update)}
                  </Button>
                </FormControl>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Box>

      {!postList.length && <Typography>Nessun post trovato</Typography>}

      <List>
        {filteredPostList.map((post, idx) => (
          <React.Fragment key={post.id}>
            {idx > 0 && <Divider component="li" />}
            <ListItem>
              <ListItemText
                primary={extractFirstText(post)}
                primaryTypographyProps={{ className: classes.primaryClamp2 }}
                secondary={
                  post.created_at
                    ? intl.formatDate(new Date(post.created_at))
                    : ""
                }
                secondaryTypographyProps={{
                  variant: "caption",
                  color: "textSecondary",
                }}
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="menu"
                  onClick={(e) => handleMenuOpen(e, post.id)}
                >
                  <MoreVert />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          </React.Fragment>
        ))}
      </List>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleEdit}>Modifica</MenuItem>
        <MenuItem onClick={handleDuplicate}>Duplica</MenuItem>
        <MenuItem onClick={handleDeleteClick}>Elimina</MenuItem>
      </Menu>

      {/* Conferma eliminazione */}
      <Confirm
        open={!!deleteRequest}
        onConfirm={onDeletePost}
        onCancel={() => setDeleteRequest(false)}
        text={intl.formatMessage(confirmMsgs.deleteElement)}
      />

      <FloatingActions>
        <Fab variant="extended" color="primary" onClick={handleAddNewPost}>
          <Add />
          {intl.formatMessage(posts.addPost)}
        </Fab>
      </FloatingActions>
    </Container>
  );
}
